import Button from '@/components/ui/button';
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { JwtPayload } from 'jwt-decode';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import useGetIdToken from '@/hooks/useGetIdToken';
import useSignOut from '@/hooks/useSignOut';
import ModuleName from './includes/ModuleName';
import { Menu, X } from 'lucide-react';
import { sidebarItemsArray } from '@/lib/main';
import useGetBasePath from '@/hooks/useGetBasePath';
import AdminPanelNavigator from './includes/AdminPanelNavigator';
import IncidentReportImg from '@/components/svg/IncidentReport';

export interface CustomJwtPayload extends JwtPayload {
  'cognito:groups'?: string[];
  given_name: string;
  family_name: string;
  email: string;
  phone_number: string;
}
const Header = () => {
  const navigate = useNavigate();
  const [isMobileSideBarOpen, setIsMobileSideBarOpen] = useState(false);
  const { handleSignOut, signOutResponse } = useSignOut();
  const basePath = useGetBasePath(0);
  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const handleSidebarOpen = useCallback(() => {
    const sidebar = document.getElementById('custom-navbar');
    if (sidebar) {
      sidebar.classList.remove('hidden');
      sidebar.classList.add('shadow-lg', 'shadow-blue-500');
    }
    setIsMobileSideBarOpen(true);
  }, []);
  const handleSidebarClose = useCallback(() => {
    const sidebar = document.getElementById('custom-navbar');
    if (sidebar) {
      sidebar.classList.add('hidden');
      sidebar.classList.remove('shadow-lg', 'shadow-blue-500');
    }
    setIsMobileSideBarOpen(false);
  }, []);
  const decodedIdToken = useGetIdToken();

  const incidentReportUrl = import.meta.env.VITE_INCIDENT_REPORT_URL;

  return (
    <>
      {' '}
      <header className="h-[75px] custom-header flex items-center justify-between bg-white drop-shadow-lg pr-4">
        <div className="flex items-center gap-x-1 mx-2">
          <Button
            className="hidden lg:block"
            variant="ghost"
            onClick={goBack}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={20}
              height={16}
              viewBox="0 0 20 16"
              fill="none">
              <path
                d="M19.2125 7.9993L1.71252 7.9993"
                stroke="#464E5F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8.71332 15L1.71332 8L8.71332 1"
                stroke="#464E5F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
          {sidebarItemsArray.includes(basePath) && (
            <>
              {' '}
              <Button
                variant={'ghost'}
                className={isMobileSideBarOpen ? 'hidden' : 'lg:hidden'}
                onClick={handleSidebarOpen}>
                <Menu color="#464E5F" />
              </Button>
              <Button
                variant={'ghost'}
                onClick={handleSidebarClose}
                className={isMobileSideBarOpen ? 'block' : 'hidden lg:hidden'}>
                <X color="#464E5F" />
              </Button>
            </>
          )}
          <ModuleName />
        </div>

        <div className="flex items-center gap-x-1">
          {incidentReportUrl && (
            <Button
              title="Incident report"
              variant={'ghost'}
              onClick={() => window.open(incidentReportUrl)}>
              <IncidentReportImg />
            </Button>
          )}
          {decodedIdToken &&
            decodedIdToken['cognito:groups']?.[0] === 'admin' && (
              <AdminPanelNavigator />
            )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={28}
                  height={28}
                  viewBox="0 0 28 28"
                  fill="none">
                  <path
                    d="M13.9556 16.1318C10.7442 16.1318 8.14076 13.5201 8.14076 10.2985C8.14076 7.07682 10.7442 4.46515 13.9556 4.46515C17.1671 4.46515 19.7705 7.07682 19.7705 10.2985C19.7705 13.5201 17.1671 16.1318 13.9556 16.1318Z"
                    fill="#98A6C4"
                  />
                  <path
                    d="M13.9557 0C21.6554 0 27.9114 6.25977 27.9114 14C27.9114 21.7241 21.6714 28 13.9557 28C6.23995 28 -1.33514e-05 21.7402 -1.33514e-05 14C-1.33514e-05 6.27586 6.25599 0 13.9557 0ZM4.55564 22.7138V22.2874C4.55564 19.5678 6.76128 17.3471 9.48024 17.3471H18.5354C21.2463 17.3471 23.46 19.5598 23.46 22.2874V22.6333C25.5213 20.3563 26.7805 17.331 26.7805 14C26.7805 6.91149 21.0458 1.15862 13.9797 1.15862C6.91367 1.15862 1.17098 6.91149 1.17098 14C1.15494 17.3713 2.4302 20.4207 4.55564 22.7138Z"
                    fill="#98A6C4"
                  />
                </svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem>
                <Link to={'/profile'}>Profile</Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={signOutResponse.isLoading}
                onClick={handleSignOut}>
                {signOutResponse.isLoading ? 'Logging out...' : 'Logout'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/">
            <h4 className="secondary-title font-medium text-[#98A6C4]">
              AVDHAAN
            </h4>
          </Link>
        </div>
      </header>
    </>
  );
};

export default Header;
